import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { useDebounce as debouncedState } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import {
  DepartmentsService,
  SchedulePersonShiftService
} from '../../../components/services';
import { DROPDOWN_FILTER_DELAY } from '../../../constants';
import saveFile from '../../../helpers/saveFile';
import { useDebounce } from '../../../hooks';
import { useApiCall } from '../../../hooks/useApiCall';
import {
  AbsenceChange,
  BaseFilter,
  LogsItem,
  selectedDate,
  ShiftChange,
  TaskChange,
  TreeNode
} from '../../../types/interfaces';
import pageSizeValues from '../../../ui/shared/paginator/pageSizeValues';
import { clearFilter, findNodeByKey, findNodesByKey, getAllKeys } from '../../../utils';
import { formatTimestamp } from '../../../utils/date-utils';
import { AbsencesExpansion } from '../AbsencesExpansion';
import { ShiftsExpansion } from '../ShiftsExpansion';
import { TasksExpansion } from '../TasksExpansion';

import { ActionDescriptionTag } from './ActionDescriptionTag';
import HistoryTableHeader from './LogsTableHeader';
import {
  getActionLabel,
  getAddedItems,
  getDeletedItems,
  getTaskInfo,
  getUpdatedItems,
  isAbsenceAction,
  isShiftAction
} from './logsUtils';

import '../../../ui/css/Logs.css';

const selectAllId = 'selectAllId';

export default function Logs() {
  const [logs, setLogs] = useState<LogsItem[]>([]);
  const [
    selectedDepartmentsFilter,
    dbouncedSelectedDepartmentsFilter,
    setSelectedDepartmentsFilter
  ] = debouncedState<TreeNode[]>([], 700);
  const [selectedDateFilter, setSelectedDateFilter] = useState<selectedDate>({
    dateFrom: '',
    dateUntil: ''
  });

  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    changedBy: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actionDate: { value: null, matchMode: FilterMatchMode.CONTAINS },
    department: { value: null, matchMode: FilterMatchMode.CONTAINS },
    person: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actionDescription: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [customFilters, setCustomFilters] = useState({ global: '' });

  const [loading, setLoading] = useState<boolean>(true);
  const [datepickerLoading, setDatepickerLoading] = useState<boolean>(true);
  const [isDepartmentsLoaded, setIsDepartmentsLoaded] = useState(false);
  const [treeItemToRemove, setTreeItemToRemove] = useState<string | number>('');
  const [treeDropDownValue, setTreeDropDownValue] = useState<TreeNode[]>([]);
  const [departments, setDepartments] = useState<TreeNode[]>([]);
  const isFirstRender = useRef(true);

  const changedByFilterRef = useRef<HTMLInputElement>(null);
  const actionDateFilterRef = useRef<HTMLInputElement>(null);
  const departmentFilterRef = useRef<HTMLInputElement>(null);
  const personFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(
    pageSizeValues[pageSizeValues.length - 1].value
  );
  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);
  const [totalCount, setTotalCount] = useState(1);

  const departmentValue = useMemo(() => {
    const isSelectAllSelected =
      findNodeByKey(selectedDepartmentsFilter, selectAllId) !== null;
    return selectedDepartmentsFilter.length === 0
      ? 'empty'
      : isSelectAllSelected
        ? getAllKeys(departments).join(',')
        : selectedDepartmentsFilter
          .map((x: { key: string }) => x.key ?? '')
          .join(',');
  }, [selectedDepartmentsFilter, departments]);

  const filtersForApi = useMemo(() => {
    const fixedFilters = Object.keys(filters)
      .map(
        (x): BaseFilter => ({
          field: x,
          value: filters[x].value,
          operator: filters[x].matchMode
        })
      )
      .concat({
        field: 'global',
        value: customFilters.global ?? '',
        operator: FilterMatchMode.CONTAINS
      })
      .concat({
        field: 'dateBetween',
        value: selectedDateFilter.dateFrom
          ? new Date(parseInt(selectedDateFilter.dateFrom)).toISOString()
          : '',
        additionalValue: selectedDateFilter.dateUntil
          ? new Date(parseInt(selectedDateFilter.dateUntil)).toISOString()
          : '',
        operator: FilterMatchMode.BETWEEN
      })
      .concat({
        field: 'departmentId',
        value: departmentValue,
        operator: FilterMatchMode.IN
      });

    return {
      pageNumber,
      pageSize,
      sortBy: multiSortMeta?.[0]?.field,
      sortDirection: multiSortMeta?.[0]?.order === 1 ? 'asc' : 'desc',
      filters: JSON.stringify(fixedFilters)
    };
  }, [
    filters,
    departmentValue,
    selectedDateFilter,
    pageNumber,
    pageSize,
    multiSortMeta,
    customFilters.global
  ]);

  const getLogs = (data: any[]) => {
    return data.map((d) => {
      // Нормализация: если added/deleted - объект, превращаем в массив
      let added = d.added
        ? Array.isArray(d.added)
          ? d.added
          : [d.added]
        : [];
      let deleted = d.deleted
        ? Array.isArray(d.deleted)
          ? d.deleted
          : [d.deleted]
        : [];
      
      if (added.length === 1 && added[0]?.taskId && !Array.isArray(d.added)) {
        added = [added[0]];
      }
      if (deleted.length === 1 && deleted[0]?.taskId && !Array.isArray(d.deleted)) {
        deleted = [deleted[0]];
      }
      
      return {
        ...d,
        added,
        deleted,
        actionDate: d.actionDate
          ? formatTimestamp(new Date(d.actionDate).getTime())
          : ''
      };
    });
  };

  const getLogsCallback = useCallback(async () => {
    setLoading(true);
    setLogs([]);
    if (selectedDepartmentsFilter.length < 1) {
      isFirstRender.current = false;
      setLogs([]);
      setLoading(false);
      return;
    }

    try {
      if (!SchedulePersonShiftService || !SchedulePersonShiftService.getLogs) {
        throw new Error('SchedulePersonShiftService.getLogs is not available');
      }
      const result = await SchedulePersonShiftService.getLogs(filtersForApi);
      if (result.data) {
        setTotalCount(
          result.data.value.total === 0 ? 1 : result.data.value.total
        );
        const filteredObjects = getLogs(result.data.value.itemsList);
        setLogs(filteredObjects);
      }
    } catch (error) {
      console.error('Ошибка загрузки логов:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: `Не удалось загрузить логи: ${error}`,
        life: 5000
      });
    } finally {
      setLoading(false);
    }
  }, [filtersForApi, selectedDepartmentsFilter.length]);

  const previousFiltersRef = useRef<string>('');

  useEffect(() => {
    if (!isDepartmentsLoaded) return;

    const newFilters = JSON.stringify(filtersForApi);
    if (newFilters !== previousFiltersRef.current) {
      previousFiltersRef.current = newFilters;
      getLogsCallback();
    }
  }, [isDepartmentsLoaded, filtersForApi, getLogsCallback]);

  const getDepartmentsCallback = useCallback(async () => {
    try {
      setIsDepartmentsLoaded(false);
      setDatepickerLoading(true); // Включаем загрузку для дат

      const urlParams = new URLSearchParams(window.location.search);
      const departmentIds = urlParams.get('departmentIds');
      const month = urlParams.get('month');
      const year = urlParams.get('year');

      const result = await DepartmentsService.getDepartments();
      if (!result.data || !Array.isArray(result.data)) {
        console.error('Invalid departments data');
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка',
          detail: 'Не удалось загрузить участки',
          life: 5000
        });
        setIsDepartmentsLoaded(true);
        setDatepickerLoading(false);
        return;
      }

      setDepartments(result.data ? [...result.data] : []);
      setTreeDropDownValue(result.data);

      // Установка фильтра по участку
      if (departmentIds) {
        const selectedDepartment = findNodesByKey(result.data!!, departmentIds.split(','));
        if (selectedDepartment) {
          setSelectedDepartmentsFilter(selectedDepartment);
        } else {
          toast.current?.show({
            severity: 'warn',
            summary: 'Предупреждение',
            detail: `Участок с ID ${departmentIds} не найден`,
            life: 5000
          });
        }
      }

      // Установка временного диапазона
      if (month && year) {
        const startDate = new Date(Number(year), Number(month) - 1, 1);
        const endDate = new Date(Number(year), Number(month), 0);
        setSelectedDateFilter({
          dateFrom: startDate.getTime().toString(),
          dateUntil: String(endDate.getTime())
        });
      }

      setIsDepartmentsLoaded(true);
      setDatepickerLoading(false);
    } catch (error) {
      console.error('Ошибка загрузки участков:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось загрузить участки',
        life: 5000
      });
      setIsDepartmentsLoaded(true);
      setDatepickerLoading(false);
    }
  }, []);

  useApiCall(getDepartmentsCallback);

  useEffect(() => {
    // Не обновляем URL, пока отделы не загружены
    if (!isDepartmentsLoaded) return;

    const params = new URLSearchParams();

    // Добавляем параметр участка
    if (selectedDepartmentsFilter.length > 0) {
      const deptIds = selectedDepartmentsFilter
        .filter((dept: { key: string }) => dept.key !== selectAllId)
        .map((dept: { key: any }) => dept.key)
        .join(',');
      if (deptIds) params.set('departmentIds', deptIds);
    }

    // Добавляем параметры месяца/года
    if (selectedDateFilter.dateFrom) {
      const date = new Date(parseInt(selectedDateFilter.dateFrom));
      if (!isNaN(date.getTime())) {
        // Проверяем валидность даты
        const month = date.getMonth() + 1;
        const year = date.getFullYear();
        params.set('month', month.toString());
        params.set('year', year.toString());
      }
    }

    // Обновляем URL без перезагрузки страницы
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(null, '', newUrl);
  }, [isDepartmentsLoaded, selectedDepartmentsFilter, selectedDateFilter]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!filters[filter]) {
      console.error(`Filter ${filter} is undefined`);
      return;
    }
    const value = e.target.value;
    const _filters = { ...filters };
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const handleSelectionChange = useCallback(
    (selection: TreeNode[] | null) => {
      if (!isDepartmentsLoaded) return;

      setPageNumber(1);
      setFirst(0);
      setSelectedDepartmentsFilter(selection as TreeNode[]);

      if (selection?.length === 0) {
        setTotalCount(1);
        toast.current?.show({
          severity: 'warn',
          detail: 'Фильтр участков сброшен'
        });
      }
    },
    [isDepartmentsLoaded]
  );

  const { debouncedCallback: debouncedHandleSelectionChange } = useDebounce(
    handleSelectionChange,
    DROPDOWN_FILTER_DELAY
  );

  const handleDateFilterChange = useCallback(
    (selection: selectedDate) => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }
      const isDateFromValid =
        selection.dateFrom !== null &&
        !isNaN(new Date(selection.dateFrom).getTime());
      const isDateUntilValid =
        selection.dateUntil !== null &&
        !isNaN(new Date(selection.dateUntil).getTime());
      const newDateFrom = isDateFromValid
        ? String(new Date(selection.dateFrom!).getTime())
        : '';
      const newDateUntil = isDateUntilValid
        ? String(new Date(selection.dateUntil!).getTime())
        : '';

      if (
        newDateFrom !== selectedDateFilter.dateFrom ||
        newDateUntil !== selectedDateFilter.dateUntil
      ) {
        setSelectedDateFilter({
          dateFrom: newDateFrom,
          dateUntil: newDateUntil
        });
        setPageNumber(1);
        setFirst(0);
      }
    },
    [selectedDateFilter]
  );

  const exportHistory = useCallback(async () => {
    try {
      // Создаем параметры для экспорта без пагинации (или с большим размером страницы)
      const exportParams = {
        sortBy: filtersForApi.sortBy,
        sortDirection: filtersForApi.sortDirection,
        filters: filtersForApi.filters
      };
      
      const result = await SchedulePersonShiftService.getLogsExcel(exportParams);
      await saveFile(result.data, 'История изменения графика.xls', '');
      
      toast.current?.show({
        severity: 'success',
        summary: 'Успешно',
        detail: 'Файл успешно экспортирован',
        life: 3000
      });
    } catch (error) {
      console.error('Ошибка экспорта:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось экспортировать файл',
        life: 5000
      });
    }
  }, [filtersForApi]);


  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const handleDeleteTagItem = (departmentIds: string | number) => {
    setTreeItemToRemove(departmentIds);
  };

  const handleFilterChange = (filterName: string, value: string) => {
    setPageNumber(1);
    setFirst(0);
    setCustomFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value
    }));
  };

  const { debouncedCallback: debouncedOnCustomFilterChange } = useDebounce(
    handleFilterChange,
    300
  );

  const handleCustomFilterChange = (filterName: string, value: string) => {
    debouncedOnCustomFilterChange(filterName, value);
  };

  const [expandedRows, setExpandedRows] =
    useState<DataTableExpandedRows | null>(null);

  const rowExpansionTemplate = (rowData: LogsItem) => {
    const addedItems = getAddedItems(rowData);
    const deletedItems = getDeletedItems(rowData);
    const actionLabel = getActionLabel(rowData.actionType);

    if (isShiftAction(rowData.actionType)) {
      const shiftAdded = addedItems.filter(
        (item): item is ShiftChange => 'workdayStart' in item
      );
      const shiftDeleted = deletedItems.filter(
        (item): item is ShiftChange => 'workdayStart' in item
      );
      const updatedItems = getUpdatedItems(
        shiftAdded,
        shiftDeleted,
        rowData.actionType
      ) as Array<{
        id: number;
        old: ShiftChange;
        new: ShiftChange;
      }>;

      return (
        <ShiftsExpansion
          rowData={rowData}
          addedItems={shiftAdded}
          deletedItems={shiftDeleted}
          updatedItems={updatedItems}
          actionLabel={actionLabel}
        />
      );
    }

    if (isAbsenceAction(rowData.actionType)) {
      const absenceAdded = addedItems.filter(
        (item): item is AbsenceChange => 'absence' in item
      );
      const absenceDeleted = deletedItems.filter(
        (item): item is AbsenceChange => 'absence' in item
      );
      const updatedItems = getUpdatedItems(
        absenceAdded,
        absenceDeleted,
        rowData.actionType
      ) as Array<{
        id: number;
        old: AbsenceChange;
        new: AbsenceChange;
      }>;

      return (
        <AbsencesExpansion
          rowData={rowData}
          addedItems={absenceAdded}
          deletedItems={absenceDeleted}
          updatedItems={updatedItems}
          actionLabel={actionLabel}
        />
      );
    }

    // Tasks actions
    const taskAdded = addedItems.filter(
      (item): item is TaskChange => 'taskId' in item
    );
    const taskDeleted = deletedItems.filter(
      (item): item is TaskChange => 'taskId' in item
    );

    return (
      <TasksExpansion
        rowData={rowData}
        addedItems={taskAdded}
        deletedItems={taskDeleted}
        getTaskInfo={getTaskInfo}
      />
    );
  };

  const actionDescriptionBodyTemplate = (rowData: LogsItem) => {
    return <ActionDescriptionTag rowData={rowData} />;
  };

  const header = (
    <HistoryTableHeader
      handleFilterChange={handleCustomFilterChange}
      selectedDepartmentsFilter={selectedDepartmentsFilter.map((department) =>
        department.label === 'Выбрать все участки'
          ? { ...department, label: 'Выбраны все участки' }
          : department
      )}
      treeDropDownValue={treeDropDownValue}
      exportHistory={exportHistory}
      handleSelectionChange={debouncedHandleSelectionChange}
      handleDateFilterChange={handleDateFilterChange}
      datepickerDate={selectedDateFilter}
      handleDeleteTagItem={handleDeleteTagItem}
      isDepartmentsLoaded={isDepartmentsLoaded}
      toast={toast}
      treeItemToRemove={treeItemToRemove}
      datepickerLoading={datepickerLoading}
    />
  );

  const getScrollHeight = () => {
    if (loading) return undefined;

    const tableElement = document.querySelector('.p-datatable-thead');
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;

    const offset = (el: Element) => {
      const rect = el.getBoundingClientRect();
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      return { top: rect.top + scrollTop };
    };

    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${tableOffset + paginatorHeight}px)`;
  };

  const [first, setFirst] = useState(0);
  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setFirst(0);
      setPageSize(event.rows);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
  }, []);

  return (
    <div className="logs-table wrapper wrapper--main">
      <Toast ref={toast} />
      <DataTable
        filters={filters}
        paginator
        first={first}
        rows={pageSize}
        onPage={onPageChanged}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${logs.length > 0 ? totalCount : 0}`}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        metaKeySelection={false}
        scrollable
        sortMode="multiple"
        removableSort
        scrollHeight={getScrollHeight()}
        size="small"
        resizableColumns
        reorderableColumns
        value={logs}
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Нет записей."
        tableStyle={{ minWidth: '50rem' }}
        expandedRows={expandedRows!}
        onRowToggle={(e) => {
          setExpandedRows(e.data as DataTableExpandedRows);
        }}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column
          columnKey="changedBy"
          field="changedBy"
          header="Пользователь"
          filter
          onFilterClear={() =>
            clearFilter(
              'changedBy',
              filters,
              setFilters,
              changedByFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={changedByFilterRef}
              onChange={handleInputChange('changedBy')}
              placeholder="Поиск по пользователю"
            />
          }
          style={{ width: '20%' }}
        />
        <Column
          columnKey="actionDate"
          field="actionDate"
          header="Дата и время действия"
          filter
          onFilterClear={() =>
            clearFilter(
              'actionDate',
              filters,
              setFilters,
              actionDateFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={actionDateFilterRef}
              onChange={handleInputChange('actionDate')}
              placeholder="Поиск по дате"
            />
          }
          style={{ width: '17%' }}
        />
        <Column
          columnKey="department"
          field="department"
          header="Участок"
          filter
          onFilterClear={() =>
            clearFilter(
              'department',
              filters,
              setFilters,
              departmentFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={departmentFilterRef}
              onChange={handleInputChange('department')}
              placeholder="Поиск по участку"
            />
          }
          style={{ width: '25%' }}
        />
        <Column
          columnKey="person"
          field="person"
          header="Сотрудник"
          filter
          onFilterClear={() =>
            clearFilter(
              'person',
              filters,
              setFilters,
              personFilterRef,
              setPageNumber,
              setFirst
            )
          }
          filterElement={
            <InputText
              ref={personFilterRef}
              onChange={handleInputChange('person')}
              placeholder="Поиск по сотруднику"
            />
          }
          style={{ width: '20%' }}
        />
        <Column expander style={{ width: '1%' }} />
        <Column
          columnKey="actionDescription"
          field="actionDescription"
          header="Описание действия"
          body={actionDescriptionBodyTemplate}
          style={{ width: '12%' }}
        />
      </DataTable>
    </div>
  );
}
